import { hip3Config } from "../config/hip3";
import type { Hip3Market } from "../types/hip3";
import { z } from "zod";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);

const finiteNumberSchema = z.number().finite();
const finiteNullableNumberSchema = finiteNumberSchema.nullable();

const hip3MarketSchema = z
  .object({
    marketId: z.string().min(1),
    provider: z.string().min(1),
    baseAsset: z.string().min(1),
    quoteAsset: z.string().min(1),
    category: z.string().optional(),
    currentPrice: finiteNumberSchema.optional(),
    volume24h: finiteNumberSchema.optional(),
    openInterest: finiteNumberSchema.optional(),
    isActive: z.boolean(),
    funding: finiteNumberSchema.optional(),
    markPx: finiteNumberSchema.optional(),
    oraclePx: finiteNumberSchema.optional(),
    maxLeverage: finiteNumberSchema.optional(),
    marginMode: z.string().optional(),
    impactBidPx: finiteNumberSchema.optional(),
    impactAskPx: finiteNumberSchema.optional(),
    estimatedSpread: finiteNumberSchema.optional(),
    spreadBps: finiteNumberSchema.optional(),
  })
  .passthrough();

const marketsResponseSchema = z
  .union([
    z.array(hip3MarketSchema),
    z.object({ markets: z.array(hip3MarketSchema) }).passthrough(),
  ])
  .transform((value) => (Array.isArray(value) ? value : value.markets));

const trendingMarketSchema = z.object({
  provider: z.string().min(1),
  marketId: z.string().min(1),
  baseAsset: z.string().min(1),
  quoteAsset: z.string().min(1),
  currentOi: finiteNullableNumberSchema,
  priorOi: finiteNullableNumberSchema,
  oiChangePct: finiteNullableNumberSchema,
  currentVolume: finiteNullableNumberSchema,
  priorVolume: finiteNullableNumberSchema,
  volumeChangePct: finiteNullableNumberSchema,
  currentPrice: finiteNullableNumberSchema,
  priceChangePct: finiteNullableNumberSchema,
  score: finiteNullableNumberSchema,
});

const marketTrendingSchema = z.object({
  meta: z.object({
    window: z.string().min(1),
    limit: z.number().int().positive(),
    currentWindowFrom: z.string().min(1),
    priorWindowFrom: z.string().min(1),
  }),
  markets: z.array(trendingMarketSchema),
});

const marketHistorySchema = z.object({
  meta: z.object({
    provider: z.string().min(1),
    marketId: z.string().min(1),
    window: z.string().min(1),
    interval: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
  }),
  candles: z.array(
    z.object({
      t: z.string().min(1),
      open: finiteNullableNumberSchema,
      high: finiteNullableNumberSchema,
      low: finiteNullableNumberSchema,
      close: finiteNullableNumberSchema,
      volume: finiteNullableNumberSchema,
      openInterest: finiteNullableNumberSchema,
      fundingAvg: finiteNullableNumberSchema,
      fundingMin: finiteNullableNumberSchema,
      fundingMax: finiteNullableNumberSchema,
      spreadBpsAvg: finiteNullableNumberSchema,
      markPxClose: finiteNullableNumberSchema,
      oraclePxClose: finiteNullableNumberSchema,
      snapshotCount: z.number().int().nonnegative(),
    })
  ),
});

const marketStatsSchema = z.object({
  meta: z.object({
    provider: z.string().min(1),
    marketId: z.string().min(1),
    window: z.string().min(1),
    currentWindowFrom: z.string().nullable(),
    priorWindowFrom: z.string().nullable(),
  }),
  price: z.object({
    open: finiteNullableNumberSchema,
    high: finiteNullableNumberSchema,
    low: finiteNullableNumberSchema,
    close: finiteNullableNumberSchema,
    changePct: finiteNullableNumberSchema,
    priorClose: finiteNullableNumberSchema,
  }),
  openInterest: z.object({
    current: finiteNullableNumberSchema,
    prior: finiteNullableNumberSchema,
    changePct: finiteNullableNumberSchema,
  }),
  funding: z.object({
    mean: finiteNullableNumberSchema,
    stddev: finiteNullableNumberSchema,
    min: finiteNullableNumberSchema,
    max: finiteNullableNumberSchema,
    positiveCount: finiteNullableNumberSchema,
    negativeCount: finiteNullableNumberSchema,
    zeroCount: finiteNullableNumberSchema,
  }),
  spread: z.object({
    avgBps: finiteNullableNumberSchema,
    minBps: finiteNullableNumberSchema,
    maxBps: finiteNullableNumberSchema,
    trend: z.enum(["tightening", "widening", "stable"]),
  }),
});

const ecosystemOverviewSchema = z.object({
  meta: z.object({
    window: z.enum(["24h", "7d", "30d"]),
    currentWindowFrom: z.string().nullable(),
    priorWindowFrom: z.string().nullable(),
  }),
  totals: z.object({
    markets: finiteNullableNumberSchema,
    providers: finiteNullableNumberSchema,
    assets: finiteNullableNumberSchema,
    categories: finiteNullableNumberSchema,
    currentOi: finiteNullableNumberSchema,
    priorOi: finiteNullableNumberSchema,
    oiChangePct: finiteNullableNumberSchema,
    currentSnapshotVolume24h: finiteNullableNumberSchema,
    priorSnapshotVolume24h: finiteNullableNumberSchema,
    snapshotVolume24hChangePct: finiteNullableNumberSchema,
  }),
});

const ecosystemHistoryPointSchema = z.object({
  t: z.string().min(1),
  value: finiteNullableNumberSchema,
});

const ecosystemHistorySchema = z.object({
  meta: z.object({
    metric: z.enum(["oi", "snapshotVolume24h", "marketCount"]),
    window: z.enum(["1d", "7d", "30d"]),
    interval: z.enum(["5m", "1h", "4h", "1d"]),
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
  points: z.array(ecosystemHistoryPointSchema),
});

const providerSummaryItemSchema = z.object({
  provider: z.string().min(1),
  markets: finiteNullableNumberSchema,
  currentOi: finiteNullableNumberSchema,
  priorOi: finiteNullableNumberSchema,
  oiChangePct: finiteNullableNumberSchema,
  oiShare: finiteNullableNumberSchema,
  priorOiShare: finiteNullableNumberSchema,
  currentSnapshotVolume24h: finiteNullableNumberSchema,
  priorSnapshotVolume24h: finiteNullableNumberSchema,
  snapshotVolume24hChangePct: finiteNullableNumberSchema,
  volumeShare: finiteNullableNumberSchema,
  priorVolumeShare: finiteNullableNumberSchema,
  spreadAvgBps: finiteNullableNumberSchema,
  fundingMean: finiteNullableNumberSchema,
  fundingStddev: finiteNullableNumberSchema,
});

const providerSummarySchema = z.object({
  meta: z.object({
    window: z.enum(["24h", "7d", "30d"]),
    sort: z.enum(["oiShare", "volumeShare", "oiChangePct", "spreadAvgBps"]),
    currentWindowFrom: z.string().nullable(),
    priorWindowFrom: z.string().nullable(),
  }),
  providers: z.array(providerSummaryItemSchema),
});

const providerHistorySeriesSchema = z.object({
  provider: z.string().min(1),
  points: z.array(
    z.object({
      t: z.string().min(1),
      value: finiteNullableNumberSchema,
    })
  ),
});

const providerHistorySchema = z.object({
  meta: z.object({
    metric: z.enum(["oi", "snapshotVolume24h", "oiShare", "volumeShare"]),
    window: z.enum(["1d", "7d", "30d"]),
    interval: z.enum(["5m", "1h", "4h", "1d"]),
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
  series: z.array(providerHistorySeriesSchema),
});

const marketLeaderboardMetricSchema = z.enum([
  "oiGrowth",
  "volumeGrowth",
  "spreadTightest",
  "spreadWidest",
  "fundingHighest",
  "fundingVolatility",
]);

const marketLeaderboardItemSchema = z.object({
  provider: z.string().min(1),
  marketId: z.string().min(1),
  baseAsset: z.string().min(1),
  quoteAsset: z.string().min(1),
  category: z.string(),
  currentOi: finiteNullableNumberSchema,
  priorOi: finiteNullableNumberSchema,
  oiChangePct: finiteNullableNumberSchema,
  currentSnapshotVolume24h: finiteNullableNumberSchema,
  priorSnapshotVolume24h: finiteNullableNumberSchema,
  snapshotVolume24hChangePct: finiteNullableNumberSchema,
  currentPrice: finiteNullableNumberSchema,
  priceChangePct: finiteNullableNumberSchema,
  spreadAvgBps: finiteNullableNumberSchema,
  fundingMean: finiteNullableNumberSchema,
  fundingStddev: finiteNullableNumberSchema,
});

const marketLeaderboardSchema = z.object({
  meta: z.object({
    metric: marketLeaderboardMetricSchema,
    window: z.enum(["24h", "7d", "30d"]),
    limit: z.number().int().positive(),
    provider: z.string().nullable(),
    category: z.string().nullable(),
  }),
  markets: z.array(marketLeaderboardItemSchema),
});

const providerLeaderboardMetricSchema = z.enum([
  "oiShare",
  "volumeShare",
  "oiGrowth",
  "volumeGrowth",
  "shareGain",
  "spreadTightest",
]);

const providerLeaderboardItemSchema = z.object({
  provider: z.string().min(1),
  currentOi: finiteNullableNumberSchema,
  priorOi: finiteNullableNumberSchema,
  oiShare: finiteNullableNumberSchema,
  priorOiShare: finiteNullableNumberSchema,
  oiShareChange: finiteNullableNumberSchema,
  currentSnapshotVolume24h: finiteNullableNumberSchema,
  priorSnapshotVolume24h: finiteNullableNumberSchema,
  volumeShare: finiteNullableNumberSchema,
  priorVolumeShare: finiteNullableNumberSchema,
  spreadAvgBps: finiteNullableNumberSchema,
  sortValue: finiteNullableNumberSchema,
});

const providerLeaderboardSchema = z.object({
  meta: z.object({
    metric: providerLeaderboardMetricSchema,
    window: z.enum(["24h", "7d", "30d"]),
    limit: z.number().int().positive(),
  }),
  providers: z.array(providerLeaderboardItemSchema),
});

const highlightMarketSchema = z.object({
  provider: z.string().min(1),
  marketId: z.string().min(1),
  label: z.string().min(1),
  value: finiteNullableNumberSchema,
});

const highlightProviderSchema = z.object({
  provider: z.string().min(1),
  value: finiteNullableNumberSchema,
});

const highlightsSchema = z.object({
  meta: z.object({
    window: z.enum(["24h", "7d"]),
  }),
  highlights: z.object({
    topOiGainer: highlightMarketSchema.nullable(),
    topVolumeGainer: highlightMarketSchema.nullable(),
    tightestSpread: highlightMarketSchema.nullable(),
    mostUnstableFunding: highlightMarketSchema.nullable(),
    topProviderShareGainer: highlightProviderSchema.nullable(),
  }),
});

export type TrendingWindow = "24h" | "1d" | "7d" | "30d";
export type MarketWindow = TrendingWindow;
export type MarketInterval = "5m" | "1h" | "4h" | "1d";
export type AnalyticsWindow = "24h" | "7d" | "30d";
export type AnalyticsHistoryWindow = "1d" | "7d" | "30d";
export type EcosystemMetric = "oi" | "snapshotVolume24h" | "marketCount";
export type ProviderHistoryMetric = "oi" | "snapshotVolume24h" | "oiShare" | "volumeShare";
export type ProviderSummarySort = "oiShare" | "volumeShare" | "oiChangePct" | "spreadAvgBps";
export type MarketLeaderboardMetric = z.infer<typeof marketLeaderboardMetricSchema>;
export type ProviderLeaderboardMetric = z.infer<typeof providerLeaderboardMetricSchema>;

export type MarketTrending = z.infer<typeof marketTrendingSchema>;
export type MarketStats = z.infer<typeof marketStatsSchema>;
export type MarketHistory = z.infer<typeof marketHistorySchema>;
export type EcosystemOverview = z.infer<typeof ecosystemOverviewSchema>;
export type EcosystemHistory = z.infer<typeof ecosystemHistorySchema>;
export type ProviderSummary = z.infer<typeof providerSummarySchema>;
export type ProviderHistory = z.infer<typeof providerHistorySchema>;
export type MarketLeaderboard = z.infer<typeof marketLeaderboardSchema>;
export type ProviderLeaderboard = z.infer<typeof providerLeaderboardSchema>;
export type HighlightsResponse = z.infer<typeof highlightsSchema>;

const trendingWindowSet = new Set<TrendingWindow>(["24h", "1d", "7d", "30d"]);
const marketWindowSet = new Set<MarketWindow>(["24h", "1d", "7d", "30d"]);
const marketIntervalSet = new Set<MarketInterval>(["5m", "1h", "4h", "1d"]);
const analyticsWindowSet = new Set<AnalyticsWindow>(["24h", "7d", "30d"]);
const analyticsHistoryWindowSet = new Set<AnalyticsHistoryWindow>(["1d", "7d", "30d"]);
const ecosystemMetricSet = new Set<EcosystemMetric>(["oi", "snapshotVolume24h", "marketCount"]);
const providerHistoryMetricSet = new Set<ProviderHistoryMetric>([
  "oi",
  "snapshotVolume24h",
  "oiShare",
  "volumeShare",
]);
const providerSummarySortSet = new Set<ProviderSummarySort>([
  "oiShare",
  "volumeShare",
  "oiChangePct",
  "spreadAvgBps",
]);
const marketLeaderboardMetricSet = new Set<MarketLeaderboardMetric>([
  "oiGrowth",
  "volumeGrowth",
  "spreadTightest",
  "spreadWidest",
  "fundingHighest",
  "fundingVolatility",
]);
const providerLeaderboardMetricSet = new Set<ProviderLeaderboardMetric>([
  "oiShare",
  "volumeShare",
  "oiGrowth",
  "volumeGrowth",
  "shareGain",
  "spreadTightest",
]);

type JsonPrimitive = string | number | boolean;

type RequestJsonOptions<TSchema extends z.ZodTypeAny> = {
  path: string;
  schema: TSchema;
  query?: Record<string, JsonPrimitive | undefined>;
  signal?: AbortSignal;
};

export class Hip3ApiError extends Error {
  readonly status?: number;
  readonly path: string;
  readonly cause?: unknown;

  constructor(message: string, options: { path: string; status?: number; cause?: unknown }) {
    super(message);
    this.name = "Hip3ApiError";
    this.status = options.status;
    this.path = options.path;
    this.cause = options.cause;
  }
}

function createApiUrl(path: string, query?: Record<string, JsonPrimitive | undefined>): URL {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(normalizedPath, `${hip3Config.apiBaseUrl}/`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value != null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url;
}

function isRetryableError(error: unknown): boolean {
  if (error instanceof Hip3ApiError) {
    if (error.status != null) {
      return RETRYABLE_STATUS_CODES.has(error.status);
    }

    const cause = error.cause;
    if (cause instanceof DOMException) {
      return cause.name === "AbortError" || cause.name === "TimeoutError";
    }

    return cause instanceof TypeError;
  }

  if (error instanceof DOMException) {
    return error.name === "AbortError" || error.name === "TimeoutError";
  }

  return error instanceof TypeError;
}

async function requestWithRetry<T>(
  run: () => Promise<T>,
  signal?: AbortSignal,
  retries = hip3Config.retries
): Promise<T> {
  let attempt = 0;

  while (true) {
    try {
      return await run();
    } catch (error) {
      if (signal?.aborted || attempt >= retries || !isRetryableError(error)) {
        throw error;
      }

      const backoffMs = hip3Config.retryDelayMs * (attempt + 1);
      await sleep(backoffMs);
      attempt += 1;
    }
  }
}

async function requestJson<TSchema extends z.ZodTypeAny>({
  path,
  schema,
  query,
  signal,
}: RequestJsonOptions<TSchema>): Promise<z.infer<TSchema>> {
  const url = createApiUrl(path, query);

  return requestWithRetry(async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), hip3Config.timeoutMs);
    const abortListener = () => controller.abort();

    signal?.addEventListener("abort", abortListener, { once: true });

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => "");
        throw new Hip3ApiError(
          `HIP-3 API returned ${response.status} ${response.statusText}${
            errorBody ? `: ${errorBody.slice(0, 250)}` : ""
          }`,
          {
            path: `${url.pathname}${url.search}`,
            status: response.status,
          }
        );
      }

      const payload = (await response.json()) as unknown;
      const parsed = schema.safeParse(payload);

      if (!parsed.success) {
        throw new Hip3ApiError(`Invalid HIP-3 API response schema for ${url.pathname}`, {
          path: `${url.pathname}${url.search}`,
          cause: parsed.error,
        });
      }

      return parsed.data;
    } catch (error) {
      if (error instanceof Hip3ApiError) {
        throw error;
      }

      throw new Hip3ApiError(`Failed to request ${url.pathname}`, {
        path: `${url.pathname}${url.search}`,
        cause: error,
      });
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", abortListener);
    }
  }, signal);
}

function assertTrendingWindow(window: TrendingWindow): TrendingWindow {
  if (!trendingWindowSet.has(window)) {
    throw new Error(`Invalid trending window "${window}"`);
  }
  return window;
}

function assertMarketWindow(window: MarketWindow): MarketWindow {
  if (!marketWindowSet.has(window)) {
    throw new Error(`Invalid market window "${window}"`);
  }
  return window;
}

function assertMarketInterval(interval: MarketInterval): MarketInterval {
  if (!marketIntervalSet.has(interval)) {
    throw new Error(`Invalid market interval "${interval}"`);
  }
  return interval;
}

function assertAnalyticsWindow(window: AnalyticsWindow): AnalyticsWindow {
  if (!analyticsWindowSet.has(window)) {
    throw new Error(`Invalid analytics window "${window}"`);
  }
  return window;
}

function assertAnalyticsHistoryWindow(window: AnalyticsHistoryWindow): AnalyticsHistoryWindow {
  if (!analyticsHistoryWindowSet.has(window)) {
    throw new Error(`Invalid analytics history window "${window}"`);
  }
  return window;
}

function assertEcosystemMetric(metric: EcosystemMetric): EcosystemMetric {
  if (!ecosystemMetricSet.has(metric)) {
    throw new Error(`Invalid ecosystem metric "${metric}"`);
  }
  return metric;
}

function assertProviderHistoryMetric(metric: ProviderHistoryMetric): ProviderHistoryMetric {
  if (!providerHistoryMetricSet.has(metric)) {
    throw new Error(`Invalid provider history metric "${metric}"`);
  }
  return metric;
}

function assertProviderSummarySort(sort: ProviderSummarySort): ProviderSummarySort {
  if (!providerSummarySortSet.has(sort)) {
    throw new Error(`Invalid provider summary sort "${sort}"`);
  }
  return sort;
}

function assertMarketLeaderboardMetric(metric: MarketLeaderboardMetric): MarketLeaderboardMetric {
  if (!marketLeaderboardMetricSet.has(metric)) {
    throw new Error(`Invalid market leaderboard metric "${metric}"`);
  }
  return metric;
}

function assertProviderLeaderboardMetric(metric: ProviderLeaderboardMetric): ProviderLeaderboardMetric {
  if (!providerLeaderboardMetricSet.has(metric)) {
    throw new Error(`Invalid provider leaderboard metric "${metric}"`);
  }
  return metric;
}

function assertLimit(limit: number): number {
  if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
    throw new Error(`Invalid limit "${limit}", expected integer between 1 and 100`);
  }
  return limit;
}

export function normalizeMarketIdForRoute(provider: string, marketId: string): string {
  const normalizedProvider = provider.trim();
  const normalizedMarketId = marketId.trim();
  const prefix = `${normalizedProvider}:`;

  return normalizedMarketId.startsWith(prefix)
    ? normalizedMarketId.slice(prefix.length)
    : normalizedMarketId;
}

export async function fetchAllMarkets(options: { signal?: AbortSignal } = {}): Promise<Hip3Market[]> {
  const markets = await requestJson({
    path: "/hip3-markets",
    schema: marketsResponseSchema,
    signal: options.signal,
  });

  if (markets.length === 0) {
    console.warn("HIP-3: backend returned no markets");
  }

  return markets as Hip3Market[];
}

export async function fetchTrendingMarkets(
  options: { window?: TrendingWindow; limit?: number; signal?: AbortSignal } = {}
): Promise<MarketTrending> {
  const window = assertTrendingWindow(options.window ?? "24h");
  const limit = assertLimit(options.limit ?? 20);

  return requestJson({
    path: "/markets/trending",
    query: { window, limit },
    schema: marketTrendingSchema,
    signal: options.signal,
  });
}

export async function fetchMarketStats(options: {
  provider: string;
  marketId: string;
  window?: MarketWindow;
  signal?: AbortSignal;
}): Promise<MarketStats> {
  const provider = options.provider.trim();
  const marketId = normalizeMarketIdForRoute(provider, options.marketId);
  const window = assertMarketWindow(options.window ?? "1d");

  if (!provider || !marketId) {
    throw new Error("provider and marketId are required");
  }

  return requestJson({
    path: `/markets/${encodeURIComponent(provider)}/${encodeURIComponent(marketId)}/stats`,
    query: { window },
    schema: marketStatsSchema,
    signal: options.signal,
  });
}

export async function fetchMarketHistory(options: {
  provider: string;
  marketId: string;
  window?: MarketWindow;
  interval?: MarketInterval;
  signal?: AbortSignal;
}): Promise<MarketHistory> {
  const provider = options.provider.trim();
  const marketId = normalizeMarketIdForRoute(provider, options.marketId);
  const window = assertMarketWindow(options.window ?? "1d");
  const interval = assertMarketInterval(options.interval ?? "1h");

  if (!provider || !marketId) {
    throw new Error("provider and marketId are required");
  }

  return requestJson({
    path: `/markets/${encodeURIComponent(provider)}/${encodeURIComponent(marketId)}/history`,
    query: { window, interval },
    schema: marketHistorySchema,
    signal: options.signal,
  });
}

export async function fetchEcosystemOverview(options: {
  window?: AnalyticsWindow;
  signal?: AbortSignal;
} = {}): Promise<EcosystemOverview> {
  const window = assertAnalyticsWindow(options.window ?? "7d");

  return requestJson({
    path: "/analytics/ecosystem/overview",
    query: { window },
    schema: ecosystemOverviewSchema,
    signal: options.signal,
  });
}

export async function fetchEcosystemHistory(options: {
  metric: EcosystemMetric;
  window?: AnalyticsHistoryWindow;
  interval?: MarketInterval;
  signal?: AbortSignal;
}): Promise<EcosystemHistory> {
  const metric = assertEcosystemMetric(options.metric);
  const window = assertAnalyticsHistoryWindow(options.window ?? "30d");
  const interval = assertMarketInterval(options.interval ?? "4h");

  return requestJson({
    path: "/analytics/ecosystem/history",
    query: { metric, window, interval },
    schema: ecosystemHistorySchema,
    signal: options.signal,
  });
}

export async function fetchProviderSummary(options: {
  window?: AnalyticsWindow;
  sort?: ProviderSummarySort;
  signal?: AbortSignal;
} = {}): Promise<ProviderSummary> {
  const window = assertAnalyticsWindow(options.window ?? "7d");
  const sort = assertProviderSummarySort(options.sort ?? "oiShare");

  return requestJson({
    path: "/analytics/providers/summary",
    query: { window, sort },
    schema: providerSummarySchema,
    signal: options.signal,
  });
}

export async function fetchProviderHistory(options: {
  metric: ProviderHistoryMetric;
  window?: AnalyticsHistoryWindow;
  interval?: MarketInterval;
  signal?: AbortSignal;
}): Promise<ProviderHistory> {
  const metric = assertProviderHistoryMetric(options.metric);
  const window = assertAnalyticsHistoryWindow(options.window ?? "30d");
  const interval = assertMarketInterval(options.interval ?? "4h");

  return requestJson({
    path: "/analytics/providers/history",
    query: { metric, window, interval },
    schema: providerHistorySchema,
    signal: options.signal,
  });
}

export async function fetchMarketLeaderboard(options: {
  metric: MarketLeaderboardMetric;
  window?: AnalyticsWindow;
  limit?: number;
  provider?: string;
  category?: string;
  signal?: AbortSignal;
}): Promise<MarketLeaderboard> {
  const metric = assertMarketLeaderboardMetric(options.metric);
  const window = assertAnalyticsWindow(options.window ?? "24h");
  const limit = assertLimit(options.limit ?? 20);

  return requestJson({
    path: "/analytics/leaderboards/markets",
    query: {
      metric,
      window,
      limit,
      provider: options.provider?.trim() || undefined,
      category: options.category?.trim() || undefined,
    },
    schema: marketLeaderboardSchema,
    signal: options.signal,
  });
}

export async function fetchProviderLeaderboard(options: {
  metric: ProviderLeaderboardMetric;
  window?: AnalyticsWindow;
  limit?: number;
  signal?: AbortSignal;
}): Promise<ProviderLeaderboard> {
  const metric = assertProviderLeaderboardMetric(options.metric);
  const window = assertAnalyticsWindow(options.window ?? "24h");
  const limit = assertLimit(options.limit ?? 20);

  return requestJson({
    path: "/analytics/leaderboards/providers",
    query: { metric, window, limit },
    schema: providerLeaderboardSchema,
    signal: options.signal,
  });
}

export async function fetchHighlights(options: {
  window?: "24h" | "7d";
  signal?: AbortSignal;
} = {}): Promise<HighlightsResponse> {
  const window = options.window ?? "24h";
  if (window !== "24h" && window !== "7d") {
    throw new Error(`Invalid highlights window "${window}"`);
  }

  return requestJson({
    path: "/analytics/highlights",
    query: { window },
    schema: highlightsSchema,
    signal: options.signal,
  });
}

export const fetchHip3Markets = fetchAllMarkets;
